/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Card as AntCard,
  Row,
  Col,
  Form,
  Spin,
  Grid,
  message,
  Input,
  Alert,
  Segmented,
} from 'antd';
import {
  LockOutlined,
  RightOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  COLORS,
  Title,
  Text,
} from 'components';
import {
  CURP_PATTERN,
  EMAIL_PATTERN,
  PHONE_NUMBER,
  RFC_PATTERN,
} from 'utils/patterns';
import _logger from 'utils/logger';
import LadaNumero from 'components/LadaNumero';
import { postSolicitarCodigoDeAcceso } from 'api/auth';
import ModalUpdateInfo from 'views/Payments/components/ModalUpdateInfo';
import useQuery from 'hooks/useQuery';
import { noSpaces } from 'utils/normalizers';
import { siteKey } from 'utils/env';
import { listToString } from 'utils/formatters';
import { Turnstile } from '@marsidev/react-turnstile';

const { useBreakpoint } = Grid;
const authOptions = [
  { label: 'RFC', value: 'rfc' },
  { label: 'CURP', value: 'curp' },
  { label: 'Teléfono', value: 'telefono_celular' },
  { label: 'Correo electrónico', value: 'correo_electronico' },
];

const SolicitarCodigo = () => {
  const screens = useBreakpoint();
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const turnstileRef = useRef();
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState();
  const [visibleUpdate, setVisibleUpdate] = useState(false);
  const [initialValues, setInitialValues] = useState();
  const [redirect] = useQuery(['redirect']);
  const redirectParam = redirect ? `?redirect=${redirect}` : '';
  const toLogin = '/mi-cuenta/login'.concat(redirectParam);
  const toRegister = '/mi-cuenta/sign-in'.concat(redirectParam);
  const authType = Form.useWatch('authType', form);

  useEffect(() => {
    if (location.state?.rfc) form.setFieldValue('rfc', location.state.rfc);
  }, []);

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      values.cf_turnstile_token = token;
      delete values.authType;
      const response = await postSolicitarCodigoDeAcceso(values);
      if (!response || response?.error) return;

      if (response?.falta_info_contacto) {
        [values.q] = Object.keys(values);
        values[values.q] = values[values.q].toUpperCase();
        setInitialValues(values);
        setVisibleUpdate(true);
        return;
      }
      setLoading(false);
      if (response.detail && typeof response.detail === 'string') {
        message.success(response?.detail);
        navigate(toLogin);
      }
    } catch (err) {
      _logger(err);
    } finally {
      turnstileRef.current.reset();
      setLoading(false);
    }
  };

  const requiredRule = { required: true };

  const rules = {
    require: [requiredRule],
    rfc: [requiredRule, { pattern: RFC_PATTERN, message: ' Ingrese un RFC valido.' }],
    curp: [requiredRule, { pattern: CURP_PATTERN, message: ' Ingrese un CURP valido.' }],
    correo_electronico: [requiredRule, { pattern: EMAIL_PATTERN, message: ' Ingrese un correo electrónico valido.' }],
    telefono_celular: [requiredRule, { pattern: PHONE_NUMBER, message: ' Ingrese un teléfono valido.' }],
  };

  return (
    <Spin spinning={loading || !!location.state?.rfc}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <Wrapper
            title={(
              <Row justify="center">
                <Title level={2}>
                  Mi Cuenta Única
                </Title>
              </Row>
            )}
            style={{ margin: `15px 0 ${screens.md ? 100 : 140}px 15px` }}
          >
            <Row justify="center">
              <Col span={24}>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={token ? onFinish : undefined}
                  initialValues={{ authType: authOptions[0].value }}
                  preserve={false}
                >
                  <Col span={24}>
                    <div>
                      <Text style={{ position: 'relative' }}>
                        <OrderedList>
                          <li>
                            Ingresa tu
                            {' '}
                            <b>
                              {listToString(authOptions.map((o) => o.label), 'disjunction')}
                            </b>
                            {' '}
                            para recibir un código de acceso.
                          </li>
                          <li>
                            Si ya estas registrado, te llegará un código a tu correo con el que
                            podrás iniciar sesión.
                          </li>
                          <li>
                            Si no tienes tus datos de contacto actualizados, deberás actualizarlos
                            para poder notificarte el código de acceso.
                          </li>
                        </OrderedList>
                      </Text>
                    </div>
                  </Col>
                  <Row gutter={[15, 15]}>
                    <Col span={24}>
                      <Form.Item name="authType" noStyle>
                        <Segmented options={authOptions} />
                      </Form.Item>
                      {authType === 'telefono_celular' ? (
                        <LadaNumero
                          form={form}
                          rules={rules}
                          names={{
                            lada: 'lada_celular',
                            numero_de_telefono: 'telefono_celular',
                          }}
                          label=""
                          placeholder={`Ingrese su ${authOptions.find((o) => o.value === authType)?.label}`}
                          fullSize
                        />
                      ) : (
                        <Form.Item
                          name={authType}
                          rules={rules[authType]}
                          normalize={(v) => noSpaces(v.toUpperCase())}
                        >
                          <Input
                            disabled={loading}
                            placeholder={`Ingrese su ${authOptions.find((o) => o.value === authType)?.label}`}
                          />
                        </Form.Item>
                      )}
                    </Col>
                  </Row>
                  <Text style={{
                    color: 'rgb(204, 204, 0)',
                    margin: '-10px 0 0 0',
                    position: 'relative',
                  }}
                  >
                    Si no te has registrado puedes hacerlo a continuación en el botón de registrarse
                  </Text>
                  <br />
                  <Row justify="space-between">
                    <Link to={toRegister}>
                      <Button
                        block={!screens.md}
                        loading={loading}
                        color={COLORS.accent}
                      >
                        Registrarse
                        <UserAddOutlined />
                      </Button>
                    </Link>
                    <Button
                      $accent
                      block={!screens.md}
                      loading={loading}
                      disabled={!token}
                      color={COLORS.accent}
                      onClick={form.submit}
                    >
                      Iniciar Sesión
                      <RightOutlined />
                    </Button>
                    <Col span={24} style={{ marginTop: 25 }}>
                      <Alert
                        type="error"
                        icon={(
                          <>
                            <LockOutlined style={{ fontSize: 32 }} />
                            <UserAddOutlined style={{ fontSize: 32 }} />
                          </>
                        )}
                        message={(
                          <ul>
                            <li>
                              <Text style={{ position: 'relative' }}>
                                Al registrarte podrás acceder a tu cuenta express que te servirá
                                para generar trámites generales.
                              </Text>
                            </li>
                            <li>
                              Si eres contribuyente con actividad empresarial, deberás registrarte
                              con este tipo de cuenta para poder consultar: mis obligaciones, mis
                              declaraciones, mi buzón, mis pagos y poder realizar trámites de las
                              obligaciones relacionadas.
                            </li>
                          </ul>
                        )}
                        showIcon
                        style={{ textAlign: 'left' }}
                      />
                    </Col>
                  </Row>
                  {!visibleUpdate && (
                    <Form.Item noStyle>
                      <Turnstile
                        siteKey={siteKey}
                        onSuccess={(cf_turnstile_token) => {
                          setToken(cf_turnstile_token);
                          if (location.state?.rfc) setTimeout(() => form.submit());
                        }}
                        onError={() => setToken(null)}
                        onExpire={() => setToken(null)}
                        ref={turnstileRef}
                      />
                    </Form.Item>
                  )}
                  <Form.Item hidden><Button htmlType="submit" /></Form.Item>
                </Form>
              </Col>
            </Row>
          </Wrapper>
        </Col>
      </Row>
      <ModalUpdateInfo
        initialValues={initialValues}
        visible={visibleUpdate}
        onCancel={() => setVisibleUpdate(false)}
        setVisible={setVisibleUpdate}
        idPadron={initialValues?.id}
        tipoDePadronId={15}
        // callback={() => {
        //   turnstileRef.current.reset();
        //   setTimeout(() => form.submit(), 3000);
        // }}
      />
    </Spin>
  );
};

const OrderedList = styled.ol`
  list-style-type: decimal;
  margin: 0;
  padding: 0;
  counter-reset: item;

  li {
    counter-increment: item;
    margin-bottom: 0.6em;

    &::marker {
      color: ${COLORS.accent} !important;
      border-radius: 50%;
      font-weight: bold;
    }
  }
`;

const Wrapper = styled(AntCard)`
  border-radius: 0.5rem;
  box-shadow: 0 .5rem 1rem rgba(0, 0, 0, 0.15);
  width: auto;
  height: auto;

  .ant-card-head {
    line-height: 1.2;
    margin-bottom: 10px;
    overflow: hidden;
    height: auto;
    .ant-typography {
      white-space: break-spaces;
    }
  }
  .ant-card-body {
    padding: 1rem 1rem;
    .ant-typography {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
      position:absolute;
    }

  }
`;

export default SolicitarCodigo;
